import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Trash2, Lock, LogOut, Download, Upload, QrCode, Image as ImageIcon, Clock, Save, ExternalLink, WifiOff } from "lucide-react";
import QRCode from "qrcode";
import { toPng } from "../lib/htmlToImage";
import Sparkles from "../components/Sparkles";
import KingdomPoster from "../components/KingdomPoster";
import {
  adminDelete,
  adminList,
  adminLogin,
  adminRegenerate,
  api,
  clearToken,
  deleteMusic,
  deletePhoto,
  featurePhoto,
  fileUrl,
  getEventConfig,
  getLoveLetters,
  getToken,
  listMusic,
  listPhotos,
  saveEventConfig,
  saveLoveLetter,
  setMusicSelected,
  setToken,
  uploadMusic,
  uploadPhoto,
} from "../lib/api";
import { isoToLocalInput, localInputToIso } from "../lib/director";
import { compressImage } from "../lib/imageCompress";
import WhatsAppPanel from "../components/WhatsAppPanel";
import WhatsAppBroadcast from "../components/WhatsAppBroadcast";
import PhotoSharePanel from "../components/PhotoSharePanel";
import DirectorControls from "../components/DirectorControls";
import YoutubeMusicPanel from "../components/YoutubeMusicPanel";

const ROLE_LIST = [
  { key: "father",        label: "నాన్న / Father" },
  { key: "mother",        label: "అమ్మ / Mother" },
  { key: "grandfather",   label: "తాతయ్య / Grandfather" },
  { key: "grandmother",   label: "అమ్మమ్మ / Maternal Grandmother" },
  { key: "peddananna",    label: "పెద్దనాన్న / Father's Elder Brother" },
  { key: "peddamma",      label: "పెద్దమ్మ / Wife of Peddananna" },
  { key: "chinnanna",     label: "చిన్నాన్న / బాబాయి / Father's Younger Brother" },
  { key: "pinni",         label: "పిన్ని / Mother's Younger Sister" },
  { key: "attha",         label: "అత్త / Father's Sister" },
  { key: "mamayya",       label: "మామయ్య / Husband of Attha" },
  { key: "friend_male",   label: "కుటుంబ స్నేహితుడు / Family Friend (Male)" },
  { key: "friend_female", label: "కుటుంబ స్నేహితురాలు / Family Friend (Female)" },
];

function KingdomQR() {
  const [qrUrl, setQrUrl] = useState("");
  const [origin, setOrigin] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = window.location.origin + "/scan";
    setOrigin(url);
    QRCode.toDataURL(url, {
      errorCorrectionLevel: "H",
      margin: 1,
      width: 720,
      color: { dark: "#4A1635", light: "#FFFFFF" },
    }).then(setQrUrl).catch(() => {});
  }, []);

  const download = () => {
    if (!qrUrl) return;
    const a = document.createElement("a");
    a.href = qrUrl;
    a.download = "anagha-kingdom-qr.png";
    a.click();
  };

  return (
    <section
      className="mb-8 glass-deep rounded-3xl p-5 sm:p-7"
      data-testid="qr-section"
    >
      <div className="flex items-center gap-2 mb-1">
        <QrCode size={20} className="text-[#B38728]" />
        <h2 className="font-display text-xl text-ink">Kingdom Invitation QR</h2>
      </div>
      <p className="text-xs text-ink-soft mb-4 max-w-xl">
        Print or share this QR on the invitation. It encodes this site's current
        URL, so if you later host the kingdom on your own domain the QR will
        automatically point to that new URL.
      </p>
      <div className="flex flex-col sm:flex-row gap-5 items-center sm:items-start">
        <div
          className="bg-white p-3 rounded-2xl"
          style={{ border: "3px solid #D4AF37", boxShadow: "0 14px 40px rgba(74,22,53,0.18)" }}
        >
          {qrUrl
            ? <img src={qrUrl} alt="Kingdom QR" width={220} height={220} data-testid="admin-qr-image" />
            : <div style={{ width: 220, height: 220, background: "#f5f5f5" }} />}
        </div>
        <div className="flex-1 w-full">
          <p className="label-eyebrow mb-1">Current URL</p>
          <p className="font-mono text-xs text-ink break-all bg-white/70 rounded-lg px-3 py-2">
            {origin || "loading…"}
          </p>
          <button
            onClick={download}
            disabled={!qrUrl}
            className="btn-royal mt-4 text-sm py-2 px-4"
            data-testid="qr-download-btn"
          >
            <Download size={14} /> Download QR
          </button>
        </div>
      </div>
    </section>
  );
}

function OfflinePanel() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const sync = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/offline/sync");
      setResult(data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || e.message });
    }
    setBusy(false);
  };

  return (
    <section className="mb-8 glass-deep rounded-3xl p-5 sm:p-7" data-testid="offline-section">
      <div className="flex items-center gap-2 mb-1">
        <WifiOff size={20} className="text-[#B38728]" />
        <h2 className="font-display text-xl text-ink">Offline Venue Kit</h2>
      </div>
      <p className="text-xs text-ink-soft mb-4 max-w-xl">
        Running the kingdom on a venue laptop without internet? Click below (while still
        online) to download every guest selfie, memory photo, music track and Anagha's
        portrait into the server's <span className="font-mono">backend/media</span> folder.
        Then start the laptop with <span className="font-mono">OFFLINE_MODE=1</span> — full
        steps in <span className="font-mono">OFFLINE_SETUP.md</span>.
      </p>
      <button
        onClick={sync}
        disabled={busy}
        className="btn-royal text-sm py-2 px-4"
        data-testid="offline-sync-btn"
      >
        <Download size={14} /> {busy ? "Downloading media…" : "Prepare Offline Media"}
      </button>
      {result && (
        <p className="text-xs mt-3 text-ink" data-testid="offline-sync-result">
          {result.error
            ? `Failed: ${result.error}`
            : `Done — ${result.downloaded} downloaded, ${result.already_local} already on disk, ${result.failed?.length || 0} failed (total ${result.total}).`}
        </p>
      )}
    </section>
  );
}

function EventSchedule() {
  const [cfg, setCfg] = useState(null);
  const [orig, setOrig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    getEventConfig().then((c) => { setCfg(c); setOrig(c); }).catch(() => {});
  }, []);

  if (!cfg) return null;
  const dirty = JSON.stringify(cfg) !== JSON.stringify(orig);

  const update = (k, v) => setCfg((c) => ({ ...c, [k]: localInputToIso(v) }));

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      const saved = await saveEventConfig(cfg);
      setOrig(saved);
      setMsg("Saved");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setCfg(orig);

  const fields = [
    ["start",  "Event Start"],
    ["cake",   "Cake Cutting"],
    ["dinner", "Dinner"],
    ["end",    "Event End"],
  ];

  return (
    <section className="mb-8 glass-deep rounded-3xl p-5 sm:p-7" data-testid="event-schedule-section">
      <div className="flex items-center gap-2 mb-1">
        <Clock size={20} className="text-[#B38728]" />
        <h2 className="font-display text-xl text-ink">Event Schedule</h2>
      </div>
      <p className="text-xs text-ink-soft mb-4 max-w-2xl">
        The Event Director (<a href="/director" className="font-mono text-plum hover:underline">/director</a>)
        auto-orchestrates every screen using these four timings. Set them in your local timezone — they&apos;ll
        be stored as UTC. For tonight&apos;s rehearsal, just set them to a few minutes from now and open the
        Director on the projector.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {fields.map(([key, label]) => (
          <div key={key} className="bg-white/70 rounded-2xl p-3" data-testid={`schedule-field-${key}`}>
            <p className="label-eyebrow mb-1">{label}</p>
            <input
              type="datetime-local"
              value={isoToLocalInput(cfg[key])}
              onChange={(e) => update(key, e.target.value)}
              className="w-full bg-transparent border border-[#D4AF37]/40 rounded-xl px-2 py-1.5 text-sm focus:outline-none focus:border-[#D4AF37]"
              data-testid={`schedule-input-${key}`}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center flex-wrap gap-3 mt-4">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="btn-royal text-sm py-2 px-4 disabled:opacity-40"
          data-testid="schedule-save-btn"
        >
          <Save size={14} /> {saving ? "Saving..." : "Save Schedule"}
        </button>
        {dirty && (
          <button onClick={reset} className="btn-ghost-royal text-sm py-2 px-4" data-testid="schedule-reset-btn">
            Reset
          </button>
        )}
        <a
          href="/director"
          target="_blank"
          rel="noreferrer"
          className="btn-ghost-royal text-sm py-2 px-4 inline-flex items-center gap-1"
          data-testid="director-open-btn"
        >
          <ExternalLink size={13} /> Open Director
        </a>
        <a
          href="/director?fastForward=1"
          target="_blank"
          rel="noreferrer"
          className="btn-ghost-royal text-sm py-2 px-4 inline-flex items-center gap-1"
          data-testid="director-preview-btn"
        >
          ⚡ 5-min Preview
        </a>
        {msg && <span className="text-xs text-plum" data-testid="schedule-msg">{msg}</span>}
      </div>
    </section>
  );
}

function MemoryPhotos() {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [featuredId, setFeaturedId] = useState(null);
  const fileRef = useRef(null);

  const refresh = async () => {
    try { setPhotos(await listPhotos()); } catch { /* ignore */ }
  };
  useEffect(() => { refresh(); }, []);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setError("");
    setUploading(true);
    try {
      for (const f of files) await uploadPhoto(await compressImage(f));
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await deletePhoto(id);
      await refresh();
    } catch { /* ignore */ }
  };

  const onFeature = async (id) => {
    try {
      await featurePhoto(id);
      setFeaturedId(id);
      setTimeout(() => setFeaturedId((cur) => (cur === id ? null : cur)), 3000);
    } catch { /* ignore */ }
  };

  return (
    <section className="mb-8 glass-deep rounded-3xl p-5 sm:p-7" data-testid="memories-section">
      <div className="flex items-center gap-2 mb-1">
        <ImageIcon size={20} className="text-[#B38728]" />
        <h2 className="font-display text-xl text-ink">Anagha's Memories Slideshow</h2>
      </div>
      <p className="text-xs text-ink-soft mb-4 max-w-2xl">
        Upload photos of Anagha (JPG / PNG, up to 20 MB each). They'll play as a
        full-screen slideshow at <span className="font-mono text-plum">/memories</span>.
        <span className="text-plum font-medium"> Click any photo below to show it on the
        screen immediately</span> — the slideshow jumps to it, then continues with the rest.
      </p>

      <div className="flex items-center gap-3 mb-4">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={onPick}
          data-testid="memories-upload-input"
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="btn-royal text-sm py-2 px-4"
          data-testid="memories-upload-btn"
        >
          <Upload size={14} /> {uploading ? "Uploading…" : "Upload Photos"}
        </button>
        <Link to="/memories" className="btn-ghost-royal text-sm py-2 px-4" data-testid="memories-preview-btn">
          ▶ Preview Slideshow
        </Link>
        <span className="text-xs text-ink-soft">{photos.length} photo{photos.length === 1 ? "" : "s"}</span>
      </div>

      {error && <p className="text-rose-700 text-sm mb-3" data-testid="memories-error">{error}</p>}

      <ul className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3" data-testid="memories-list">
        {photos.map((p) => (
          <li
            key={p.id}
            className="relative rounded-xl overflow-hidden bg-white/70 group"
            data-testid={`memory-${p.id}`}
            style={{ aspectRatio: "1 / 1" }}
          >
            <button
              onClick={() => onFeature(p.id)}
              className="w-full h-full block cursor-pointer"
              title="Show this photo on the screen now"
              data-testid={`memory-feature-${p.id}`}
            >
              <img src={fileUrl(p.path)} alt={p.name} className="w-full h-full object-cover" />
              {featuredId === p.id ? (
                <span className="absolute inset-0 grid place-items-center bg-plum/60 text-white text-[11px] font-medium">
                  ✓ Showing on screen
                </span>
              ) : (
                <span className="absolute inset-0 grid place-items-center bg-plum/45 text-white text-[11px] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  ▶ Show Now
                </span>
              )}
            </button>
            <button
              onClick={() => onDelete(p.id)}
              className="absolute top-1 right-1 bg-rose-100/95 hover:bg-rose-200 text-rose-700 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Remove"
              data-testid={`memory-delete-${p.id}`}
            >
              <Trash2 size={12} />
            </button>
          </li>
        ))}
        {!photos.length && (
          <li className="col-span-full text-center py-6 text-ink-soft text-sm">
            No memory photos yet. Upload some above to start the slideshow.
          </li>
        )}
      </ul>
    </section>
  );
}


function LoveLetters() {
  const [letters, setLetters] = useState({});
  const [saving, setSaving] = useState({});
  useEffect(() => { getLoveLetters().then(setLetters).catch(() => {}); }, []);
  const update = (role, value) => setLetters((s) => ({ ...s, [role]: value }));
  const save = async (role) => {
    setSaving((s) => ({ ...s, [role]: true }));
    try {
      await saveLoveLetter(role, letters[role] || "");
    } catch {}
    setSaving((s) => ({ ...s, [role]: false }));
  };
  return (
    <section className="mb-8 glass-deep rounded-3xl p-5 sm:p-7" data-testid="love-letters-section">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-2xl">💌</span>
        <h2 className="font-display text-xl text-ink">Love Letters from Anagha (per role)</h2>
      </div>
      <p className="text-xs text-ink-soft mb-4">
        These messages override the AI-generated thank-you note. Use <code className="px-1 bg-white/60 rounded">{"{name}"}</code> to insert the guest&apos;s name. Telugu and English both supported.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {ROLE_LIST.map((r) => (
          <div key={r.key} className="bg-white/70 rounded-2xl p-3" data-testid={`letter-${r.key}`}>
            <p className="label-eyebrow mb-1">{r.label}</p>
            <textarea
              value={letters[r.key] || ""}
              onChange={(e) => update(r.key, e.target.value)}
              rows={3}
              placeholder="Write a custom thank-you message... use {name} for the guest's name"
              className="w-full bg-transparent border border-[#D4AF37]/40 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]"
              style={{ fontFamily: "'Noto Sans Telugu',sans-serif" }}
              data-testid={`letter-input-${r.key}`}
            />
            <button
              onClick={() => save(r.key)}
              disabled={saving[r.key]}
              className="mt-1 btn-royal text-xs py-1.5 px-3"
              data-testid={`letter-save-${r.key}`}
            >
              {saving[r.key] ? "Saving…" : "Save"}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  generating: "bg-indigo-100 text-indigo-800 border-indigo-200",
  ready: "bg-blue-100 text-blue-800 border-blue-200",
  approved: "bg-emerald-100 text-emerald-900 border-emerald-200",
  rejected: "bg-rose-100 text-rose-800 border-rose-200",
  failed: "bg-red-100 text-red-800 border-red-200",
};

function LoginForm({ onAuth }) {
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { token } = await adminLogin(pwd);
      setToken(token);
      onAuth();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-6">
      <Sparkles count={18} />
      <form
        onSubmit={submit}
        className="glass-deep rounded-3xl p-8 w-full max-w-md relative"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-2 mb-1">
          <Lock size={16} className="text-[#B38728]" />
          <p className="label-eyebrow">royal council</p>
        </div>
        <h1 className="font-display text-3xl font-semibold text-ink">Admin Dashboard</h1>
        <p className="text-ink-soft text-sm mt-1">Enter the royal cipher to proceed.</p>

        <label className="block label-eyebrow mt-6 mb-2">Password</label>
        <input
          type="password"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          className="w-full bg-transparent border-0 border-b-2 border-[#D4AF37]/40 px-0 py-2 text-lg focus:outline-none focus:border-[#D4AF37]"
          data-testid="admin-password-input"
          autoFocus
        />
        {err && <p className="text-rose-700 text-sm mt-2" data-testid="admin-login-error">{err}</p>}

        <button
          type="submit"
          disabled={loading}
          className="btn-royal w-full mt-6"
          data-testid="admin-login-submit"
        >
          {loading ? "Verifying..." : "Enter"}
        </button>

        <Link to="/welcome" className="block text-center text-xs label-eyebrow mt-4 text-ink-soft hover:text-plum">
          ← back to the gates
        </Link>
      </form>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(Boolean(getToken()));
  const [guests, setGuests] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState({});
  const [showPoster, setShowPoster] = useState(false);
  const posterRef = useRef(null);

  const refresh = async () => {
    try {
      const data = await adminList();
      setGuests(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearToken();
        setAuthed(false);
      }
    }
  };

  useEffect(() => {
    if (!authed) return;
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [authed]);

  const filtered = useMemo(() => {
    if (filter === "all") return guests;
    return guests.filter((g) => g.status === filter);
  }, [guests, filter]);

  const approvedGuests = useMemo(
    () => guests.filter((g) => g.status === "ready" || g.status === "approved"),
    [guests]
  );

  const setBusyFor = (id, val) => setBusy((b) => ({ ...b, [id]: val }));

  const act = (fn) => async (id) => {
    setBusyFor(id, true);
    try {
      await fn(id);
      await refresh();
    } catch {}
    setBusyFor(id, false);
  };

  const onRegenerate = act(adminRegenerate);
  const onDelete = act(adminDelete);

  const downloadPoster = async () => {
    if (!posterRef.current) return;
    try {
      const dataUrl = await toPng(posterRef.current, { pixelRatio: 2 });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "kingdom-of-anagha.png";
      a.click();
    } catch (e) {
      alert("Could not export poster: " + e.message);
    }
  };

  if (!authed) return <LoginForm onAuth={() => setAuthed(true)} />;

  const counts = guests.reduce(
    (acc, g) => {
      acc[g.status] = (acc[g.status] || 0) + 1;
      acc.all += 1;
      return acc;
    },
    { all: 0 }
  );

  return (
    <div
      className="min-h-screen px-6 sm:px-10 py-8"
      style={{
        backgroundImage:
          "linear-gradient(180deg, rgba(255,240,245,0.9), rgba(253,251,247,0.95)), url('https://images.unsplash.com/photo-1638043008124-9268465173b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBwaW5rJTIwbGF2ZW5kZXIlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4MTU5NDU5N3ww&ixlib=rb-4.1.0&q=85')",
        backgroundSize: "cover",
        backgroundAttachment: "fixed",
      }}
      data-testid="admin-dashboard"
    >
      <header className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <p className="label-eyebrow">royal council</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-ink">Kingdom Administration</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowPoster((v) => !v)} className="btn-ghost-royal text-sm py-2 px-4" data-testid="admin-toggle-poster">
            {showPoster ? "Hide" : "Show"} Poster
          </button>
          <button onClick={downloadPoster} className="btn-royal text-sm py-2 px-4" data-testid="admin-download-poster" disabled={!approvedGuests.length}>
            <Download size={14} /> Download Poster
          </button>
          <button
            onClick={() => {
              clearToken();
              setAuthed(false);
            }}
            className="btn-ghost-royal text-sm py-2 px-4"
            data-testid="admin-logout-btn"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      {/* Kingdom QR for printing on invitations */}
      <KingdomQR />

      {/* Offline venue kit — media sync for no-internet events */}
      <OfflinePanel />

      {/* Director live controls — Start Cake, Start Dinner, Resume Auto */}
      <DirectorControls />

      {/* WhatsApp poster outreach to guests */}
      <WhatsAppPanel />

      {/* Admin's own WhatsApp numbers + custom broadcast message */}
      <WhatsAppBroadcast />

      {/* Upload guest photos → share links → WhatsApp */}
      <PhotoSharePanel />

      {/* Event Director schedule editor */}
      <EventSchedule />

      {/* Memory photos slideshow */}
      <MemoryPhotos />

      {/* YouTube background music per Director scene */}
      <YoutubeMusicPanel />

      {/* Love Letters editor */}
      <LoveLetters />

      {/* Stats */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          ["all", "All"],
          ["generating", "Brewing"],
          ["ready", "Live"],
          ["failed", "Failed"],
        ].map(([k, label]) => (
          <button
            key={k}
            data-testid={`filter-${k}`}
            onClick={() => setFilter(k)}
            className={`rounded-2xl p-4 text-left border transition-all ${
              filter === k
                ? "bg-white border-[#D4AF37] shadow-md"
                : "bg-white/60 border-white/40 hover:bg-white"
            }`}
          >
            <p className="label-eyebrow">{label}</p>
            <p className="font-display text-2xl text-ink mt-1">{counts[k] || 0}</p>
          </button>
        ))}
      </section>

      {/* Poster preview (always rendered offscreen for export) */}
      <div
        className={showPoster ? "mb-8" : "absolute -left-[9999px] top-0 w-[1280px]"}
        aria-hidden={!showPoster}
      >
        <KingdomPoster ref={posterRef} guests={approvedGuests} />
      </div>

      {/* Guests grid */}
      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
        data-testid="admin-guests-grid"
      >
        {filtered.map((g) => (
          <article
            key={g.id}
            className="bg-white/90 backdrop-blur-md rounded-2xl border border-[#D4AF37]/30 p-3 shadow-sm"
            data-testid={`admin-guest-${g.id}`}
          >
            <div className="flex gap-3">
              <div className="w-20 h-20 rounded-xl bg-blush overflow-hidden flex-shrink-0">
                <img
                  src={fileUrl(g.character_path || g.selfie_path)}
                  alt={g.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-xl text-ink truncate">{g.name}</p>
                <p className="text-xs text-ink-soft truncate">{g.role_title}</p>
                <span className={`mt-1 inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_COLORS[g.status] || ""}`}>
                  {g.status}
                </span>
              </div>
            </div>

            {g.error && (
              <p className="text-xs text-rose-700 mt-2 line-clamp-2">{g.error}</p>
            )}

            <div className="grid grid-cols-2 gap-1 mt-3">
              <button
                onClick={() => onRegenerate(g.id)}
                disabled={busy[g.id]}
                className="text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-40 rounded-lg py-2 grid place-items-center"
                title="Regenerate"
                data-testid={`regenerate-${g.id}`}
              >
                <RefreshCw size={16} className={busy[g.id] ? "animate-spin" : ""} />
              </button>
              <button
                onClick={() => {
                  if (window.confirm(`Delete ${g.name}?`)) onDelete(g.id);
                }}
                disabled={busy[g.id]}
                className="text-rose-700 bg-rose-50 hover:bg-rose-100 disabled:opacity-40 rounded-lg py-2 grid place-items-center"
                title="Delete"
                data-testid={`delete-${g.id}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </article>
        ))}
        {filtered.length === 0 && (
          <p className="col-span-full text-center text-ink-soft py-12 label-eyebrow">No guests in this view yet</p>
        )}
      </section>
    </div>
  );
}
